import React, { createContext, useContext } from "react";

/**
 * Signature shared by every post-click handler that flows through `PostInfo`
 * (`handleRedirect` / `handleShop` / `handleTagged`): `(postId, mediaUrl, platform, name, userId)`.
 */
export type PostClickHandler = (
  postId: string,
  mediaUrl: string,
  platform: any,
  name: any,
  userId: string,
) => void;

/**
 * Optional overrides for the default (generic `Track.ts`) post-click handlers.
 *
 * Provided only on surfaces that need context-aware tracking the shared post
 * components can't reach on their own — e.g. the Website Template search page,
 * which must stamp `page_key` on every post/shop/tagged click but renders
 * `FeedOptimised` directly (bypassing `FeedControls`). Any handler left
 * undefined falls back to the component's own prop handler.
 */
export type PostClickTrackingValue = {
  handleRedirect?: PostClickHandler;
  handleShop?: PostClickHandler;
  handleTagged?: PostClickHandler;
};

const PostClickTrackingContext = createContext<PostClickTrackingValue | null>(
  null,
);

export function PostClickTrackingProvider({
  value,
  children,
}: {
  value: PostClickTrackingValue;
  children: React.ReactNode;
}) {
  return (
    <PostClickTrackingContext.Provider value={value}>
      {children}
    </PostClickTrackingContext.Provider>
  );
}

/**
 * Returns the active post-click tracking overrides, or `null` on normal feed
 * surfaces (no provider mounted). Safe to call unconditionally.
 */
export function usePostClickTracking(): PostClickTrackingValue | null {
  return useContext(PostClickTrackingContext);
}
